import React, { useState } from 'react';
import { Box, Button, Card, Typography, CardContent } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuth } from "../../context/authContext";
import { uploadNews } from '../../services/newsServices';
import Swal from 'sweetalert2';
import { ActionLoader } from '../../components/loader';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import ImageUploaderWithCrop from '../../components/images/ImageUploaderWithCrop';
import ArrowBack from '../../components/common/ArrowBack';
import TooltipHelper from '../../components/common/TooltipHelper';
import SectionTips from '../../components/common/SectionTips';
import { Info } from 'lucide-react';

const NewsForm = () => {
    const [preview, setPreview] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const categories = ['Política', 'Deportes', 'Tecnología', 'Ciencias e investigación', 'Medioambiente', 'Industria y negocios', 'Educación', 'Economía', 'Cultura y entretenimiento'];
    const { user } = useAuth();
    const [resetCounter, setResetCounter] = useState(0);
    // Configuración de módulos para el editor de lead (solo negrita)
    const leadModules = {
        toolbar: [
            ['bold'],
        ],
    };

    // Configuración de módulos para el editor de descripción (sin H1)
    const descriptionModules = {
        toolbar: [
            [{ header: [2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    // Esquema de validación con Yup
    const validationSchema = Yup.object({
      title: Yup.string().required('El título es obligatorio'),
      lead: Yup.string().required('La bajada es obligatoria'),
      description: Yup.string().required('La descripción es obligatoria'),
      creationDate: Yup.date()
      .required('La fecha de publicación es obligatoria')
      .max(new Date(), 'La fecha no puede ser posterior a hoy'),
      category: Yup.string().required('La categoría es obligatoria'),
      image: Yup.mixed()
      .required('La imagen es obligatoria')
      .test(
        'fileSize',
        'El archivo es demasiado grande. Máximo 1MB.',
        (value) => {
          if (!value) return false; // No hay archivo
          return value.size <= 2 * 1024 * 1024; // 2MB
        }
      )
  });

    const initialValues = {
        title: '',
        description: '',
        lead: '',
        category: '',
        creationDate: '',
        image: null
    };

    const onSubmit = async (values, { resetForm }) => {
      const formData = new FormData();
      const fields = ["title", "description", "lead", "category", "creationDate"];
      
      fields.forEach(field => {
          formData.append(field, values[field]);
      });
      
      if (values.image) {
          formData.append("image", values.image);
      }

      const isAdmin = user.role === 'admin';
      const status = isAdmin ? "approved" : "pending";
      const confirmationText = isAdmin
          ? "¿Deseas publicar esta noticia?"
          : "¿Deseas enviar esta noticia para revisión?";
      const successTitle = isAdmin
          ? "Noticia publicada exitosamente"
          : "Noticia enviada";
      const successText = isAdmin
          ? null
          : "La noticia fue enviada con éxito para revisión";

      try {
          const confirmation = await Swal.fire({
              title: '¿Estás seguro?',
              text: confirmationText,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#4338ca',
              cancelButtonColor: '#6b7280',
              confirmButtonText: isAdmin ? 'Sí, publicar' : 'Sí, enviar',
              cancelButtonText: 'Cancelar'
          });

          if (!confirmation.isConfirmed) return;

          formData.append("status", status);
          formData.append("companyRut", user.rut);
          setSubmitting(true);
          const response = await uploadNews(formData, user);
          setSubmitting(false);

          if (response) {
              await Swal.fire({
                  title: successTitle,
                  text: successText || undefined,
                  icon: 'success',
                  confirmButtonColor: '#4338ca',
                  confirmButtonText: 'Aceptar'
              });
              handleReset(resetForm);
              if (isAdmin) navigate('/news');
          }
      } catch (error) {
          Swal.fire({
              title: 'Error',
              text: isAdmin
                  ? 'Hubo un error al publicar la noticia'
                  : 'Hubo un error al enviar la noticia',
              icon: 'error',
              confirmButtonColor: '#4338ca',
              confirmButtonText: 'Aceptar'
          });
      } finally {
          setSubmitting(false);
      }
  };


    const handlePreview = (isValid) => {
        if (isValid) {
            setPreview(true);
        }
    };

    const handleReset = (resetForm) => {
        setPreview(false);
        setImagePreview(null);
        resetForm();
        setResetCounter(prev => prev + 1);
    };

    

    const inputClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none shadow-sm";
    const selectClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none appearance-none bg-white shadow-sm";
    const labelClass = "block mb-2 font-medium text-gray-700";
    const helperTextClass = "text-xs text-gray-500 mt-1";
    const sectionClass = "p-6 bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-xl";
    const titleClass = "flex items-center mb-6 pb-2 border-b border-gray-200";
    const tipsList = [
      'Debes completar todos los campos obligatorios.',
      'Recuerda que las noticias deben ser aprobadas previamente por un administrador.',
      <div className="flex items-center gap-1">
      <span>Puedes guiarte mediante los iconos de información para entender mejor cada campo.</span>
      <Info className="w-4 h-4 text-[#0488AF] cursor-pointer" />
      </div>,
    ];
    return (
      <Box className="w-full max-w-7xl overflow-x-hidden bg-white rounded-xl shadow-md p-4 md:p-6 justify-center items-center mx-auto">
        <Box className="max-w-6xl mx-auto">
          {/* Cabecera */}
          {!preview ? <ArrowBack /> : null}
          <div className="bg-gradient-to-b from-[#09274A] to-[#0488AF] rounded-xl p-8 mb-6 shadow-lg">
            <h2 className="text-3xl font-bold text-center text-white">
            {preview ? "Vista Previa de la Noticia" : "Crear Tu Noticia"}
            </h2>
            <p className="text-center text-indigo-100 mt-3 font-medium">
            {preview ? "Revisa cómo se verá tu noticia antes de publicarla" : "Todos los campos son obligatorios"}
            </p>
          </div>
          {!preview ? 
          <SectionTips
            section="crear una noticia"
            buttonTitle="¿Cómo usar esta sección?"
            tipsTitle="¿Cómo crear una noticia?"
            tipsList={tipsList}
            finalNote="Consejo: Asegúrate de revisar la vista previa antes de enviar la noticia."
            color="#1976d2" // tu color institucional si usas otro
          />
          : null}


          {/* Contenedor principal */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ values, errors, touched, isValid, dirty,handleChange, setFieldValue, resetForm, handleBlur, setFieldTouched, setFieldError  }) => (
              !preview ? (
                <Form className="space-y-8">
                  {/* Card: Información Principal */}
                  <div className={sectionClass}>
                    <div className={titleClass}>
                    <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                      </svg>
                    </div>
                      <h3 className="text-xl font-semibold text-gray-800">Información Principal</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                      <label className={`${labelClass} flex items-center gap-1`}>
                        Título de la noticia <span className="text-red-500">*</span>
                        <TooltipHelper text="Este título se mostrará en la parte superior de la noticia y en las listas de noticias." />
                      </label>
                      <input
                        type="text"
                        name="title"
                        className={`${inputClass} ${touched.title && errors.title ? 'border-red-500' : ''}`}
                        placeholder="Título de la noticia"
                        value={values.title}
                        onChange={(e) => setFieldValue('title', e.target.value)}
                        onBlur={handleBlur}
                        />
                        {touched.title && errors.title && (
                        <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                        )}
                      </div>
                      <div>
                      <label className={`${labelClass} flex items-center gap-1`}>
                        Categoría de la noticia <span className="text-red-500">*</span>    
                        <TooltipHelper text="Selecciona la categoría que mejor describe la noticia." />
                      </label>
                        <select
                        name="category"
                        className={selectClass}
                        value={values.category}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        >
                        <option value="">Seleccione una categoría</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                        </select>
                        {touched.category && errors.category && (
                        <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                        )}
                      </div>
                      <div>
                      <label className={`${labelClass} flex items-center gap-1`}>
                        Fecha de publicación <span className="text-red-500">*</span>
                        <TooltipHelper text="Selecciona la fecha en la que deseas que se publique la noticia. No se pueden seleccionar fechas posteriores a la de hoy" />
                      </label>
                      <input
                        type="date"
                        name="creationDate"
                        className={inputClass}
                        value={values.creationDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        max={new Date().toISOString().split("T")[0]}
                      />
                      {touched.creationDate && errors.creationDate && (
                        <p className="text-red-500 text-sm mt-1">{errors.creationDate}</p>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* Card: Contenido */}
                  <div className={sectionClass}>
                    <div className={titleClass}>
                    <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6M9 16h6M9 8h6M4 6h16c1.104 0 2 .896 2 2v12c0 1.104-.896 2-2 2H4c-1.104 0-2-.896-2-2V8c0-1.104.896-2 2-2z"/>
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">Contenido</h3>
                    </div>
                    <div className="space-y-6 px-4 md:px-6 pb-6">
                    <div className="mb-12"> {/* Añadimos un margen inferior */}
                      <label  className={`${labelClass} flex items-center gap-1`}>
                        Bajada de la noticia  <span className="text-red-500">*</span> 
                        <TooltipHelper text="El texto se mostrará como un resumen o introducción a la noticia. Por defecto se mostrara en cursiva, puede destacar palabras usando la herramienta de negrita" />
                      </label>
                      <div className="border rounded-md">
                      <ReactQuill
                        theme="snow"
                        value={values.lead}
                        onChange={(value) => setFieldValue('lead', value)}
                        modules={leadModules}
                        style={{ minHeight: "100px" }}
                      />
                      </div>
                      {touched.lead && errors.lead && (
                      <p className="text-red-500 text-sm mt-1">{errors.lead}</p>
                      )}
                    </div>

                    <div className="mt-16"> {/* Añadimos un margen superior */}
                      <label className={`${labelClass} flex items-center gap-1`}>
                        Descripción completa  <span className="text-red-500">*</span> 
                        <TooltipHelper text="Es es el cuerpo principal de la noticia. Puedes usar el editor para dar formato al texto, agregar enlaces, etc. Para agregar salto entre parrafos solo debe presionar enter" />  
                      </label>
                      <div className="border rounded-md">
                      <ReactQuill
                        theme="snow"
                        value={values.description}
                        onChange={(value) => setFieldValue('description', value)}
                        modules={descriptionModules}
                        style={{ minHeight: "150px"}}
                      />
                      </div>
                      {touched.description && errors.description && (
                      <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                      )}
                    </div>
                    </div>
                  </div>
                  {/* Card: Imagen */}
                  <div className={sectionClass}>
                    <div className={titleClass}>
                      <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3h14c1.104 0 2 .896 2 2v14c0 1.104-.896 2-2 2H5c-1.104 0-2-.896-2-2V5c0-1.104.896-2 2-2zM10 9l2 2 4-4" />
                        </svg>
                      </div>
                      <div className='flex items-center gap-1'>
                      <h3 className="text-xl font-semibold text-gray-800">Imagen Principal</h3> <TooltipHelper text="La imagen se mostrará al principio de la noticia. Puedes usar la herramienta de recorte para tus imagenes." />
                      </div>
                    </div>
                    <div className="px-4 md:px-6 pb-6">
                      <div className="flex flex-col md:flex-row items-start gap-6">
                        <div className="w-full md:w-1/2">
                        <ImageUploaderWithCrop
                            aspectRatio={16/9}
                            onCropped={(blob) => {
                              const url = URL.createObjectURL(blob);
                              setFieldValue("image", blob);
                              setImagePreview(url);
                              setFieldTouched("image", true, false);
                            }}
                            setFieldTouched={setFieldTouched}
                            setFieldError={setFieldError}
                            maxFileSize={2097152}  // Personaliza según necesites (2MB en este ejemplo)
                            fieldName="image"      // Nombre del campo en Formik
                            values={values}    // Valores de Formik
                            resetKey={resetCounter}
                            setFieldValue={setFieldValue}
                          />
                          <p className={helperTextClass}>Formatos: JPG, PNG. Tamaño máximo: 2MB</p>
                          {touched.image && errors.image && (
                            <p className="text-red-500 text-sm mt-1">{errors.image}</p>
                          )}
                        </div>

                        <div className="w-full md:w-1/2 flex justify-center items-center border border-dashed border-gray-300 rounded-lg p-4 min-h-40">
                          {imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Vista previa"
                              className="max-h-48 object-contain"
                            />
                          ) : (
                            <p className="text-gray-400 text-center text-sm">Vista previa de la imagen</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  
                  {/* Botones de acción */}
                  <div className="flex flex-col md:flex-row gap-4 px-4 md:px-6 pb-6 space-y-4 md:space-y-0">
                    <Button
                      variant="outlined"
                      onClick={() => handleReset(resetForm)}
                      fullWidth
                      className="py-3"
                      sx={{ 
                        textTransform: 'none',
                        borderColor: '#DC3465',
                        color: '#DC3465',
                        '&:hover': {
                          backgroundColor: '#C42251',
                          color: 'white',
                        },
                      }}
                      >
                        Limpiar formulario
                    </Button>
                    <Button
                    variant="contained"
                    onClick={() => handlePreview(isValid && dirty)}
                    disabled={!(isValid && dirty && values.image)}
                    fullWidth
                    className="py-3"
                    style={{
                      textTransform: 'none',
                      backgroundColor: (isValid && dirty && values.image) ? '#0488AF' : '#94a3b8',
                      color: '#ffffff'
                    }}
                    >
                    Ver vista previa
                    </Button>
                  </div>
                    {!isValid && Object.keys(errors).length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                      <h4 className="font-semibold text-red-700 mb-2">Faltan campos o hay errores:</h4>
                      <ul className="list-disc list-inside text-red-600 text-sm">
                        {Object.entries(errors).map(([field, errorMsg]) => (
                          <li key={field}>{errorMsg}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Form>
              ) : (
                <Box className="bg-white rounded-xl shadow-md overflow-hidden">
                  {/* Vista previa del contenido de la noticia */}
                    {/* Imagen destacada */}
                    {imagePreview && (
                      <Box className="w-full h-80 bg-gray-800 relative">
                        <img
                          src={imagePreview}
                          alt="Imagen principal"
                          className="w-full h-full object-cover opacity-95"
                        />
                        <Box className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent h-40"></Box>
                        <Box className="absolute top-4 left-6">
                          <span className="bg-indigo-700 text-white px-3 py-1 rounded-full text-sm font-medium">
                            {values.category}
                          </span>
                        </Box>
                        <Box className="absolute bottom-6 left-6 right-6 text-white">
                          <Typography variant="h4" className="font-bold mb-2 text-white">
                            {values.title}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    <Box className="p-8 max-w-4xl mx-auto">
                      {!imagePreview && (
                        <>
                          <Box className="mb-8">
                            <Typography variant="h3" className="font-bold mb-4 text-gray-900">
                              {values.title}
                            </Typography>
                            <Box className="flex items-center gap-4">
                              <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                                {values.category}
                              </span>
                              <span className="text-gray-500 text-sm">
                                {values.creationDate}
                              </span>
                            </Box>
                          </Box>
                        </>
                      )}
                      
                      <Box className="mb-12">
                          <Card sx={{ 
                            borderLeft: '4px solid #1976d2', 
                            mb: { xs: 3, sm: 4, md: 5 }, 
                            bgcolor: '#f7f9fc',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Typography 
                                    component="div"
                                    sx={{ 
                                        fontSize: { xs: '1rem', sm: '1.125rem' },
                                        fontWeight: 500,
                                        fontStyle: 'italic',
                                        lineHeight: 1.6
                                    }}
                                    style={{
                                      wordWrap: 'break-word', // Asegura que el texto largo se ajuste correctamente
                                      whiteSpace: 'normal', // Permite los saltos de línea cuando sea necesario
                                    }}
                                    dangerouslySetInnerHTML={{ __html: values.lead }}
                                />
                            </CardContent>
                        </Card>
                                            
                        <div
                            className="prose prose-lg max-w-none prose-p:my-4 prose-p:text-gray-800 prose-img:rounded-lg"
                            style={{
                              wordWrap: 'break-word', // Asegura que el texto largo se ajuste correctamente
                              whiteSpace: 'normal', // Permite los saltos de línea cuando sea necesario
                            }}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(values.description || ''),
                            }}
                          />
                                        </Box>
                                        
                                        <Box className="flex justify-center pt-4 border-t gap-x-4">
                                          <Button
                                              variant="contained"
                                              type="submit"
                                              className="py-3 px-12"
                                              style={{ 
                                                  textTransform: 'none',
                                                  backgroundColor: '#1BBB55',
                                                  color: '#ffffff',
                                                  '&:hover': {
                                                    backgroundColor: '#1aab51',
                                                    color: '#ffffff',
                                                  },
                                              }}
                                              onClick={() => onSubmit(values, { resetForm })}
                                          >
                                              {user.role === 'admin' ? 'Publicar noticia' : 'Enviar para revisión'}
                                          </Button>
                                          <Button
                                              variant="outlined"
                                              onClick={() => setPreview(false)}
                                              className="py-3 px-12 border-gray-300"
                                              sx={{ 
                                                textTransform: 'none',
                                                borderColor: '#DC3465',
                                                color: '#DC3465',
                                                '&:hover': {  
                                                  backgroundColor: '#C42251',
                                                  color: 'white',
                                                },
                                              }}
                                          >
                                              Volver a editar
                                          </Button>
                                      </Box>
                                    </Box>
                            </Box>
                        )
                    )}
                </Formik>
            </Box>
            <ActionLoader open={isSubmitting} onClose={() => {}} />
        </Box>
    );
};

export default NewsForm;